import {
  isAnalyticsConsentGranted,
  parseCookieYesConsent,
} from '@/lib/cookieyes';
import { isPostHogCapturing, posthog } from '@/lib/posthog';
import { User } from '@/types/user';
import { getAccessRole, getLinkedPartners } from '@/utils/rbac';

export const POSTHOG_SCHEMA_VERSION = '3.0.1';

const REPORT_SUPER_PROPERTY_KEYS = [
  'report_id',
  'report_date',
  'report_brand',
  'brand_id',
  'brand_name',
  'report_lang',
  'schema_version',
  'consent_status',
] as const;

export const getEmailDomain = (email: string): string | undefined => {
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex < 0 || atIndex === normalized.length - 1) return undefined;
  return normalized.slice(atIndex + 1);
};

const omitUndefined = <T extends Record<string, unknown>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== '')
  );

export const INTERNAL_PARTNER_ID = 'internal';

const collectPartnerIds = (user: User): string[] => {
  const linked = getLinkedPartners(user).map((partner) => partner.id);
  const company = user.company_id?.trim();
  return [...new Set([...linked, company].filter((id): id is string => Boolean(id)))];
};

export const buildPersonProperties = (user: User) => {
  const ids = collectPartnerIds(user);
  const role = getAccessRole(user);
  const isClientOrGuest = role === 'CLIENT' || role === 'GUEST';
  const partnerId = ids[0] || (isClientOrGuest ? undefined : INTERNAL_PARTNER_ID);

  return omitUndefined({
    partner_id: partnerId,
    partner_ids: ids.length > 1 ? ids : undefined,
    role: user.role,
    email_domain: getEmailDomain(user.email),
  });
};

export const identifyPostHogUser = (user: User): void => {
  if (!isPostHogCapturing()) return;

  posthog.identify(user.id, buildPersonProperties(user));
};

export type ReportSuperPropertiesInput = {
  reportId: string;
  reportDate?: string | Date | null;
  reportBrand?: string | null;
  brandId?: string | null;
  reportLang?: string | null;
};

export const getReportBrandEventProperties = ({
  brandId,
  brandName,
  reportLang,
}: {
  brandId?: string | null;
  brandName?: string | null;
  reportLang?: string | null;
}) =>
  omitUndefined({
    brand_id: brandId ?? undefined,
    brand_name: brandName ?? undefined,
    report_lang: reportLang ?? undefined,
  });

export const registerReportSuperProperties = ({
  reportId,
  reportDate,
  reportBrand,
  brandId,
  reportLang,
}: ReportSuperPropertiesInput): void => {
  if (!isPostHogCapturing()) return;

  const consent = parseCookieYesConsent();
  const consentStatus = isAnalyticsConsentGranted(consent) ? 'granted' : 'denied';

  const dateValue =
    reportDate instanceof Date
      ? reportDate.toISOString()
      : reportDate
        ? String(reportDate)
        : undefined;

  posthog.register(
    omitUndefined({
      report_id: reportId,
      report_date: dateValue,
      report_brand: reportBrand ?? undefined,
      brand_id: brandId ?? undefined,
      brand_name: reportBrand ?? undefined,
      report_lang: reportLang ?? undefined,
      schema_version: POSTHOG_SCHEMA_VERSION,
      consent_status: consentStatus,
    })
  );
};

export const unregisterReportSuperProperties = (): void => {
  if (!isPostHogCapturing()) return;
  REPORT_SUPER_PROPERTY_KEYS.forEach((key) => {
    posthog.unregister(key);
  });
};
